import {
    Component,
    defineElement,
    FC,
    onDidLoad,
    useBindMethod,
    useElement,
    useElements,
    useProp,
} from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { ButtonSquare } from '@atoms/button-square/button-square';
import { Button } from '@atoms/button/button';
import { Image } from '@atoms/image/image';
import { KrpanoElement } from '@molecules/krpano/krpano';
import Slider from '@molecules/slider-embla/slider-embla';
import { SliderInterface } from '@molecules/slider-embla/utilities/slider-embla';
import { BPDVideoProps } from '@molecules/video/utilities/video';
import { Video } from '@molecules/video/video';
import { closeModal, openModal } from '@source/store/modals';
import { updateSelectedPano } from '@store/krpano';
import { useStylesheet } from '@utilities/hooks';
import { isMobile, useMedia } from '@utilities/media-query';
import { EmblaOptionsType } from 'embla-carousel';

import style from './media-overlay.scss';

type MediaOverlayView = 'image' | 'vr' | 'video';

interface MediaOverlayImage {
    image: string;
    alt: string;
}

interface MediaOverlayScene {
    xmlUrl: string;
    name: string;
}

interface MediaOverlayVideo {
    videoId: string;
    name: string;
}

interface MediaOverlayMediaData {
    images?: MediaOverlayImage[];
    scenes?: MediaOverlayScene[];
    videos?: MediaOverlayVideo[];
}

interface OpenOverlayOptions {
    index?: number;
    vr?: boolean;
    video?: boolean;
}

export interface MediaOverlayInterface extends Component {
    open: (options?: OpenOverlayOptions) => void;
}

const HIDDEN_CLASS = 'u-hidden';
const ACTIVE_CLASS = 'is-active';
const MEDIA_OVERLAY_KRPANO_TOUR_ID = 'media-overlay-vr-tour';

export const MediaOverlay: FC<MediaOverlayInterface> = ({ update }) => {
    const [media] = useProp<string>('media');
    const [modalId] = useProp<string>('modalId', 'media-overlay');
    const [zoomImage] = useProp<boolean>('zoomImage', false);
    const [sidebar] = useProp<boolean>('sidebar', false);
    const [allowMultipleVideos] = useProp<boolean>('allowMultipleVideos', false);

    const slider = useElement<SliderInterface>('[js-hook-media-overlay-slider]');
    const sidebarSlider = useElement<SliderInterface>('[js-hook-media-overlay-sidebar-slider]');
    const sliderContainer = useElement<HTMLDivElement>('[js-hook-media-overlay-slider-container]');
    const sidebarImageButtons = useElements<HTMLButtonElement[]>(
        '[js-hook-media-overlay-sidebar-slider-button-image]',
    );
    const galleryLength = useElement<HTMLParagraphElement>(
        '[js-hook-media-overlay-gallery-length]',
    );
    const sidebarVrButton = useElement<HTMLDivElement>('[js-hook-media-overlay-sidebar-button-vr]');
    const sidebarVideoButton = useElement<HTMLDivElement>(
        '[js-hook-media-overlay-sidebar-button-video]',
    );

    const vrContainer = useElement<HTMLDivElement>('[js-hook-media-overlay-vr-container]');
    const vrKrpano = useElement<KrpanoElement>('[js-hook-media-overlay-krpano]');
    const vrSceneButtons = useElements<HTMLButtonElement[]>(
        '[js-hook-media-overlay-vr-scene-button]',
    );

    const videoContainer = useElement<HTMLDivElement>('[js-hook-media-overlay-video-container]');
    const videoElement = useElement<BPDVideoProps>('[js-hook-video]');
    const videoButtons = useElements<HTMLButtonElement[]>('[js-hook-media-overlay-video-button]');

    const { images = [], scenes = [], videos = [] } = JSON.parse(media) as MediaOverlayMediaData;
    let isMobileDevice = isMobile();
    let currentMediaView: MediaOverlayView;

    const sidebarSliderOptions = JSON.stringify({
        axis: isMobileDevice ? 'x' : 'y',
        align: 'center',
        containScroll: 'trimSnaps',
        inViewThreshold: undefined,
        dragFree: true,
    } as EmblaOptionsType);

    useStylesheet(style);

    onDidLoad(() => {
        sidebar &&
            useMedia(() => {
                const newIsMobile = isMobile();
                if (isMobileDevice !== newIsMobile) {
                    isMobileDevice = newIsMobile;
                    update();
                }
            });
    });

    const setMediaView = (options: OpenOverlayOptions) => {
        if (options.vr) {
            sliderContainer.current?.classList.add(HIDDEN_CLASS);
            vrContainer.current?.classList.remove(HIDDEN_CLASS);
            videoContainer.current?.classList.add(HIDDEN_CLASS);
            sidebarVrButton.current?.classList.add(ACTIVE_CLASS);
            sidebarVideoButton.current?.classList.remove(ACTIVE_CLASS);

            setVirtualScene(scenes![0], 0);
            videoElement.current?.unloadVimeoVideo();
            currentMediaView = 'vr';
        } else if (options.video) {
            sliderContainer.current?.classList.add(HIDDEN_CLASS);
            vrContainer.current?.classList.add(HIDDEN_CLASS);
            videoContainer.current?.classList.remove(HIDDEN_CLASS);
            sidebarVrButton.current?.classList.remove(ACTIVE_CLASS);
            sidebarVideoButton.current?.classList.add(ACTIVE_CLASS);

            setVideo(videos![0], 0);
            vrKrpano.current?.destroy();
            currentMediaView = 'video';
        } else {
            sliderContainer.current?.classList.remove(HIDDEN_CLASS);
            vrContainer.current?.classList.add(HIDDEN_CLASS);
            videoContainer.current?.classList.add(HIDDEN_CLASS);
            sidebarVrButton.current?.classList.remove(ACTIVE_CLASS);
            sidebarVideoButton.current?.classList.remove(ACTIVE_CLASS);

            if (options.index) {
                slider.current?.select(options.index, { jump: true });
            } else {
                onSelect(0);
            }

            vrKrpano.current?.destroy();
            videoElement.current?.unloadVimeoVideo();
            currentMediaView = 'image';
        }
    };

    const selectSlide = (index: number) => {
        if (currentMediaView !== 'image') {
            setMediaView({ index });
        } else {
            slider.current?.select(index);
        }
    };

    const onSelect = (index: number) => {
        if (!sidebar) return;

        galleryLength.current!.innerText = `${index + 1} / ${images.length}`;
        sidebarSlider.current?.select(index);
        Array.from(sidebarImageButtons.current).forEach((thumbnail, i) => {
            thumbnail?.classList[i === index ? 'add' : 'remove'](ACTIVE_CLASS);
        });
    };

    const open = (options?: OpenOverlayOptions) => {
        setMediaView(options ?? { index: 0 });
        requestAnimationFrame(() => {
            openModal(modalId);
        });
    };

    useBindMethod('open', open);

    const handleClose = () => {
        closeModal(modalId);
        vrKrpano.current?.destroy && vrKrpano.current?.destroy();
        videoElement.current?.unloadVimeoVideo && videoElement.current?.unloadVimeoVideo();
    };

    const setVirtualScene = (scene: MediaOverlayScene, index: number) => {
        Array.from(vrSceneButtons.current).forEach((button, i) =>
            button?.classList[i === index ? 'add' : 'remove'](ACTIVE_CLASS),
        );

        updateSelectedPano(MEDIA_OVERLAY_KRPANO_TOUR_ID, scene.xmlUrl);
    };

    const setVideo = (video: MediaOverlayVideo, index: number) => {
        Array.from(videoButtons.current).forEach((button, i) =>
            button?.classList[i === index ? 'add' : 'remove'](ACTIVE_CLASS),
        );

        videoElement.current?.loadVimeoVideo(video.videoId);
    };

    return (
        <bpd-modal class="c-media-overlay" id={modalId} onModalClose={handleClose}>
            <div class="media-overlay__container">
                <ButtonSquare
                    onClick={handleClose}
                    variant="light"
                    svg="icons-close"
                    ariaLabel="Close button"
                    classes="media-overlay__close-button"
                />
                {sidebar && (
                    <aside class="media-overlay__sidebar">
                        <div class="media-overlay__sidebar-inner">
                            <Slider.Container
                                classes="media-overlay__sidebar-slider"
                                options={sidebarSliderOptions}
                                variant={isMobileDevice ? 'default' : 'vertical'}
                                controlMode="start-end"
                                js-hook-media-overlay-sidebar-slider
                                showFade>
                                {!isMobileDevice && images.length && (
                                    <Fragment>
                                        <Slider.Control
                                            variant="light"
                                            direction="up"
                                            ariaLabel="Previous slide"
                                            classes="media-overlay__sidebar-slider-control media-overlay__sidebar-slider-control--up"
                                        />
                                        <Slider.Control
                                            variant="light"
                                            direction="down"
                                            ariaLabel="Next slide"
                                            classes="media-overlay__sidebar-slider-control media-overlay__sidebar-slider-control--down"
                                        />
                                    </Fragment>
                                )}
                                <Slider.List>
                                    {images.map((image, index) => (
                                        <Slider.Slide classes="media-overlay__sidebar-slider-item">
                                            <Button
                                                classes="media-overlay__sidebar-button media-overlay__sidebar-button--image"
                                                hook="media-overlay-sidebar-slider-button-image"
                                                onClick={() => selectSlide(index)}>
                                                <Image
                                                    classes="media-overlay__thumbnail"
                                                    image={image.image}
                                                    alt={image.alt}
                                                    settings={{
                                                        '240w': {
                                                            width: 88,
                                                            height: 88,
                                                        },
                                                    }}
                                                />
                                            </Button>
                                        </Slider.Slide>
                                    ))}
                                </Slider.List>
                            </Slider.Container>

                            <p
                                class="u-body-normal media-overlay__gallery-length"
                                js-hook-media-overlay-gallery-length>
                                0 / {images.length}
                            </p>

                            {scenes.length ? (
                                <Button
                                    classes="media-overlay__sidebar-button media-overlay__sidebar-button--special"
                                    variant="grey-90"
                                    icon="icons-360"
                                    label="360"
                                    hook="media-overlay-sidebar-button-vr"
                                    onClick={() => setMediaView({ vr: true })}
                                />
                            ) : null}

                            {videos.length ? (
                                <Button
                                    classes="media-overlay__sidebar-button media-overlay__sidebar-button--special"
                                    variant="grey-90"
                                    icon="icons-video"
                                    label="Video"
                                    hook="media-overlay-sidebar-button-video"
                                    onClick={() => setMediaView({ video: true })}
                                />
                            ) : null}
                        </div>
                    </aside>
                )}
                <div class="media-overlay__inner">
                    {images.length && (
                        <div
                            class="media-overlay__media-container"
                            js-hook-media-overlay-slider-container>
                            {zoomImage ? (
                                <div class="media-overlay__slider">
                                    <div class="media-overlay__item">
                                        <zoom-image
                                            url={images[0].image}
                                            alt={images[0].alt}
                                            js-hook-zoom-image
                                            settings={{
                                                '1920w': {
                                                    width: 1920,
                                                },
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <Slider.Container
                                    centered="true"
                                    variant="full-height"
                                    classes="media-overlay__slider"
                                    onSelect={onSelect}
                                    js-hook-media-overlay-slider>
                                    {images.length ? (
                                        <div class="media-overlay__slider-controls">
                                            <Slider.Control
                                                variant="light"
                                                direction="left"
                                                ariaLabel="Previous slide"
                                                classes="media-overlay__slider-control"
                                            />
                                            <Slider.Control
                                                variant="light"
                                                direction="right"
                                                ariaLabel="Next slide"
                                                classes="media-overlay__slider-control"
                                            />
                                        </div>
                                    ) : null}
                                    <Slider.List>
                                        {images.map((image) => (
                                            <Slider.Slide classes="media-overlay__slider-item">
                                                <div class="media-overlay__item">
                                                    <Image
                                                        classes="media-overlay__image"
                                                        image={image.image}
                                                        alt={image.alt}
                                                        settings={{
                                                            '1920w': {
                                                                width: 1920,
                                                                height: 1440,
                                                            },
                                                        }}
                                                    />
                                                </div>
                                            </Slider.Slide>
                                        ))}
                                    </Slider.List>
                                </Slider.Container>
                            )}
                        </div>
                    )}
                    {scenes.length ? (
                        <div
                            class="media-overlay__media-container"
                            js-hook-media-overlay-vr-container>
                            <bpd-krpano
                                js-hook-media-overlay-krpano
                                class="media-overlay__krpano"
                                krpano-id={`media-overlay-krpano`}
                                store-id={MEDIA_OVERLAY_KRPANO_TOUR_ID}
                            />
                            {scenes.length > 1 ? (
                                <div class="media-overlay__media-item-buttons">
                                    {scenes.map((scene, index) => (
                                        <Button
                                            label={scene.name}
                                            variant="grey-5"
                                            classes="media-overlay__media-item-button"
                                            hook="media-overlay-vr-scene-button"
                                            onClick={() => setVirtualScene(scene, index)}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    {videos.length ? (
                        <div
                            class="media-overlay__media-container"
                            js-hook-media-overlay-video-container>
                            <Video
                                classes={`media-overlay__video ${
                                    allowMultipleVideos && videos.length > 1
                                        ? 'media-overlay__video--has-multiple'
                                        : ''
                                }`}
                                platform="vimeo"
                                id={videos[0].videoId}
                                instanceId="media-overlay-video"
                                ignoreViewport
                                playsInline
                                controls
                                loop
                            />
                            {(allowMultipleVideos && videos.length) > 1 ? (
                                <div class="media-overlay__media-item-buttons">
                                    {videos.map((video, index) => (
                                        <Button
                                            label={video.name}
                                            variant="grey-5"
                                            classes="media-overlay__media-item-button"
                                            hook="media-overlay-video-button"
                                            onClick={() => setVideo(video, index)}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </bpd-modal>
    );
};

MediaOverlay.props = {
    media: {
        type: String,
        required: true,
    },
    modalId: {
        type: String,
        required: false,
    },
    zoomImage: {
        type: Boolean,
        required: false,
    },
    sidebar: {
        type: Boolean,
        required: false,
    },
    allowMultipleVideos: {
        type: Boolean,
        required: false,
    },
};

defineElement('bpd-media-overlay', MediaOverlay);
